import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { associationContextActions } from "./associationContext-slice";
import { selectTokenUserId } from "./auth-slice";
import { getMyAssociations } from "../services/associationService";
import { AssociationResponse } from "../types/association.types";
import {
  readRememberedAssociationId,
  rememberAssociationId,
} from "../utils/associationSelection";

/**
 * Discriminated so a caller reconciling an editor against server truth can
 * tell "the list is this now" from "I learned nothing, keep what you have".
 */
export type AssociationsFetchResult =
  | { ok: true; associations: AssociationResponse[] }
  | { ok: false };

/** The last `/mine` sequence number handed out; never goes back. */
let lastRequestSeq = 0;

/**
 * ONE shared fetch of `GET /association/mine`, consumed by the context loader
 * and by every post-mutation re-read. Never rejects: a failure is recorded in
 * the slice and reported through the result. An aborted request (unmount,
 * dependency change) records nothing, because the slice may already belong
 * to the next persona. For the same reason an answer that lands after a
 * logout, or after another user signed in, is discarded: it belongs to a
 * session that no longer exists.
 *
 * Overlapping calls are sequenced: each one takes the next number from a
 * counter that never goes back (not even across a logout), and the slice
 * applies only the answer to the latest started request. The result still
 * reports the caller's OWN answer, which is server truth from after that
 * request started (a card's post-save re-read rebases on it); it is the
 * shared context that a superseded answer must not overwrite.
 */
export const fetchMyAssociations = (
  userId: number,
  signal?: AbortSignal,
): ThunkAction<
  Promise<AssociationsFetchResult>,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch, getState) => {
    const sameSession = () => selectTokenUserId(getState()) === userId;
    lastRequestSeq += 1;
    const seq = lastRequestSeq;
    dispatch(associationContextActions.fetchStart(seq));
    try {
      const associations = await getMyAssociations(signal);
      if (!sameSession()) {
        return { ok: false };
      }
      dispatch(
        associationContextActions.fetchSucceeded({
          associations,
          rememberedId: readRememberedAssociationId(userId),
          seq,
        }),
      );
      return { ok: true, associations };
    } catch (error) {
      if (signal?.aborted || !sameSession()) {
        return { ok: false };
      }
      console.error("AssociationContext: fetch failed", error);
      dispatch(associationContextActions.fetchFailed(seq));
      return { ok: false };
    }
  };
};

/** The switcher's choice: remembered for the session, then applied. */
export const chooseAssociation = (
  userId: number,
  associationId: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return (dispatch) => {
    rememberAssociationId(userId, associationId);
    dispatch(associationContextActions.selected(associationId));
  };
};
